import React, { useState, useEffect } from 'react';
import './FileTransmissions.css';

const FileTransmissions = () => {
    const [transmissions, setTransmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTransmission, setSelectedTransmission] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [directionFilter, setDirectionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [lotNumberFilter, setLotNumberFilter] = useState('');
    const [poNumberFilter, setPoNumberFilter] = useState('');
    const [retrying, setRetrying] = useState(false);

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api/v1';

    useEffect(() => {
        fetchTransmissions();
        const interval = setInterval(fetchTransmissions, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, [searchTerm, directionFilter, statusFilter, lotNumberFilter, poNumberFilter]);

    const fetchTransmissions = async () => {
        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (directionFilter) params.append('direction', directionFilter);
            if (statusFilter) params.append('status', statusFilter);
            if (lotNumberFilter) params.append('lot_number', lotNumberFilter);
            if (poNumberFilter) params.append('purchase_order_number', poNumberFilter);

            const response = await fetch(`${API_BASE_URL}/transmissions?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch transmissions');

            const data = await response.json();
            setTransmissions(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching transmissions:', err);
            setError('Failed to load transmission logs');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (id, type = 'xml') => {
        try {
            const response = await fetch(`${API_BASE_URL}/transmissions/${id}/download?type=${type}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to get download URL');
            }

            const { url } = await response.json();
            window.open(url, '_blank');
        } catch (err) {
            console.error('Download error:', err);
            alert(`Download failed: ${err.message}`);
        }
    };

    const handleRetry = async (tx) => {
        if (!confirm(`Retry sending ${tx.filename.split('/').pop()}?`)) return;

        setRetrying(true);
        try {
            // 1. Get signed URL for the XML file
            const dlResponse = await fetch(`${API_BASE_URL}/transmissions/${tx.id}/download?type=xml`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (!dlResponse.ok) throw new Error('Failed to get file download URL');
            const { url } = await dlResponse.json();

            // 2. Fetch the XML content
            const xmlResponse = await fetch(url);
            if (!xmlResponse.ok) throw new Error('Failed to download XML file');
            const xmlContent = await xmlResponse.text();

            // 3. Re-send via AS2
            const sendResponse = await fetch(`${API_BASE_URL}/as2/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    xml: xmlContent,
                    filename: tx.filename.split('/').pop()
                })
            });

            const result = await sendResponse.json();
            if (!sendResponse.ok) throw new Error(result.message || 'Failed to resend file');

            alert(`File resent successfully. New transmission #${result.transmissionId}`);
            setSelectedTransmission(null);
            fetchTransmissions();
        } catch (err) {
            console.error('Retry error:', err);
            alert(`Retry failed: ${err.message}`);
        } finally {
            setRetrying(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'RECEIVED': return <span className="badge badge-info">Received</span>;
            case 'TRANSFER_INITIATED': return <span className="badge badge-warning">In Progress</span>;
            case 'COMPLETED': return <span className="badge badge-success">Completed</span>;
            case 'FAILED': return <span className="badge badge-danger">Failed</span>;
            case 'BLOCKED': return <span className="badge badge-danger">Blocked</span>;
            default: return <span className="badge badge-warning">{status}</span>;
        }
    };

    if (loading && transmissions.length === 0) return <div className="loading">Loading logs...</div>;

    return (
        <>
            <header className="top-header">
                <div className="breadcrumb">
                    <span>Operations</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">AS2 Logs</span>
                </div>
            </header>
            <div className="file-transmissions-page">
                <div className="page-header">
                    <div className="header-title">
                        <h1>AS2 Transmission Logs</h1>
                        <p className="subtitle">Track and manage your file transfers</p>
                    </div>
                    <button className="refresh-btn" onClick={fetchTransmissions}>
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        Refresh
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="filters-bar">
                    <div className="search-input">
                        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by filename, partner ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)}>
                            <option value="">All Directions</option>
                            <option value="INBOUND">Inbound</option>
                            <option value="OUTBOUND">Outbound</option>
                        </select>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Statuses</option>
                            <option value="RECEIVED">Received</option>
                            <option value="TRANSFER_INITIATED">In Progress</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="FAILED">Failed</option>
                            <option value="BLOCKED">Blocked</option>
                        </select>
                    </div>
                </div>

                {/* EPCIS Search Filters */}
                <div className="epcis-filters-bar">
                    <div className="epcis-filter-input">
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by Lot Number..."
                            value={lotNumberFilter}
                            onChange={(e) => setLotNumberFilter(e.target.value)}
                        />
                    </div>
                    <div className="epcis-filter-input">
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by Purchase Order..."
                            value={poNumberFilter}
                            onChange={(e) => setPoNumberFilter(e.target.value)}
                        />
                    </div>
                    {(lotNumberFilter || poNumberFilter) && (
                        <button
                            className="clear-filters-btn"
                            onClick={() => { setLotNumberFilter(''); setPoNumberFilter(''); }}
                        >
                            Clear EPCIS Filters
                        </button>
                    )}
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="table-container">
                    <table className="transmissions-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Time</th>
                                <th>Direction</th>
                                <th>Status</th>
                                <th>Filename</th>
                                <th>Lot #</th>
                                <th>PO #</th>
                                <th>Sender</th>
                                <th>Receiver</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transmissions.map((tx) => (
                                <tr key={tx.id} onClick={() => setSelectedTransmission(tx)} className="clickable-row">
                                    <td className="entry-id-cell">#{tx.id}</td>
                                    <td>{new Date(tx.created_at).toLocaleString()}</td>
                                    <td>
                                        <span className={`direction-tag ${tx.direction.toLowerCase()}`}>
                                            {tx.direction}
                                        </span>
                                    </td>
                                    <td>{getStatusBadge(tx.status)}</td>
                                    <td className="filename-cell" title={tx.filename}>
                                        {tx.filename.split('/').pop()}
                                    </td>
                                    <td className="lot-cell">{tx.lot_number || '-'}</td>
                                    <td className="po-cell">{tx.purchase_order_number || '-'}</td>
                                    <td>{tx.sender_name}</td>
                                    <td>{tx.receiver_name}</td>
                                    <td className="actions-cell">
                                        <button className="view-btn" onClick={(e) => { e.stopPropagation(); setSelectedTransmission(tx); }}>View</button>
                                    </td>
                                </tr>
                            ))}
                            {transmissions.length === 0 && (
                                <tr>
                                    <td colSpan="10" className="no-data">No file transmissions found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {selectedTransmission && (
                    <div className="modal-overlay" onClick={() => setSelectedTransmission(null)}>
                        <div className="modal-minimal" onClick={e => e.stopPropagation()}>
                            <button className="close-x" onClick={() => setSelectedTransmission(null)}>&times;</button>

                            <div className="modal-top">
                                <div className="status-row">
                                    <span className="entry-id-badge">#{selectedTransmission.id}</span>
                                    {getStatusBadge(selectedTransmission.status)}
                                    <span className={`direction-pill ${selectedTransmission.direction.toLowerCase()}`}>
                                        {selectedTransmission.direction}
                                    </span>
                                </div>
                                <div className="filename-display">
                                    {selectedTransmission.filename.split('/').pop()}
                                </div>
                                <div className="meta-row">
                                    <span>{new Date(selectedTransmission.created_at).toLocaleString()}</span>
                                    <span className="separator">•</span>
                                    <span>{selectedTransmission.sender_name}</span>
                                    <span className="arrow">→</span>
                                    <span>{selectedTransmission.receiver_name}</span>
                                </div>
                                {(selectedTransmission.lot_number || selectedTransmission.purchase_order_number) && (
                                    <div className="epcis-info-row">
                                        {selectedTransmission.lot_number && (
                                            <span className="epcis-tag lot">
                                                <strong>Lot:</strong> {selectedTransmission.lot_number}
                                            </span>
                                        )}
                                        {selectedTransmission.purchase_order_number && (
                                            <span className="epcis-tag po">
                                                <strong>PO:</strong> {selectedTransmission.purchase_order_number}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="download-row">
                                <button className="dl-btn xml" onClick={() => handleDownload(selectedTransmission.id, 'xml')}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                    </svg>
                                    EPCIS XML
                                </button>
                                {(selectedTransmission.mdn_filename || selectedTransmission.direction === 'INBOUND') ? (
                                    <button className="dl-btn mdn" onClick={() => handleDownload(selectedTransmission.id, 'mdn')}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                        </svg>
                                        MDN
                                    </button>
                                ) : selectedTransmission.status === 'TRANSFER_INITIATED' ? (
                                    <span className="mdn-unavailable">Awaiting MDN...</span>
                                ) : selectedTransmission.mdn_message_id ? (
                                    <span className="mdn-unavailable">MDN received (file not stored)</span>
                                ) : (
                                    <span className="mdn-unavailable">No MDN</span>
                                )}
                            </div>

                            {selectedTransmission.mdn_message_id && (
                                <div className="mdn-id-row">
                                    <strong>MDN ID:</strong> <code>{selectedTransmission.mdn_message_id}</code>
                                    {selectedTransmission.mdn_subject && (
                                        <div className="mdn-subject"><strong>Subject:</strong> {selectedTransmission.mdn_subject}</div>
                                    )}
                                </div>
                            )}

                            {selectedTransmission.direction === 'OUTBOUND' && (
                                <div className="retry-row">
                                    <button
                                        className={`retry-btn ${selectedTransmission.status === 'FAILED' ? 'retry-btn-danger' : ''}`}
                                        onClick={() => handleRetry(selectedTransmission)}
                                        disabled={retrying}
                                    >
                                        {retrying ? (
                                            'Resending...'
                                        ) : (
                                            <>
                                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                                </svg>
                                                {selectedTransmission.status === 'FAILED' ? 'Retry Send' : 'Resend'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {selectedTransmission.error_message && (
                                <div className="error-notice">
                                    <strong>Error:</strong> {selectedTransmission.error_message}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default FileTransmissions;
